import { SEO } from "@/components/SEO";
import { ArrowLeft } from "lucide-react";
import { Link, useLocation } from "wouter";

export default function PrivacyPolicy() {
  const [, setLocation] = useLocation();

  return (
    <>
      <SEO
        title="Privacy Policy | Ganji Drinks - Healthy Ragi Drinks & Traditional Indian Beverages"
        description="Privacy Policy for Ganji Drinks - India's premium ready-to-drink ragi beverage brand. Learn how we protect your data when you shop healthy traditional millet drinks online."
        keywords="ragi drinks privacy, healthy ragi beverages data protection, traditional Indian drinks privacy policy, millet drinks online privacy, Ganji Drinks data security, ragi malt drinks privacy, organic ragi beverages"
      />

      <div
        className="min-h-screen"
        style={{ backgroundColor: "hsl(var(--ganji-warm-cream))" }}
      >
        {/* Empty Header / Spacer (Logo is global) */}
        <nav className="p-4 md:p-6 h-20 pointer-events-none sticky top-0 z-40 bg-transparent"></nav>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link href="/">
              <a className="inline-flex items-center gap-2 hover:opacity-75 transition-opacity"
                style={{ color: 'hsl(var(--ganji-rustic-brown))' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5" />
                  <path d="M12 19l-7-7 7-7" />
                </svg>
                <span className="text-lg" style={{ fontFamily: "'Patrick Hand', cursive" }}>Back</span>
              </a>
            </Link>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <h1
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{
                color: "hsl(var(--ganji-rustic-brown))",
                fontFamily: "'Permanent Marker', cursive"
              }}
            >
              Privacy Policy
            </h1>

            <p
              className="text-lg mb-8"
              style={{ color: "hsl(var(--ganji-rustic-brown))" }}
            >
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>

            <div className="space-y-8">
              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Introduction
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  At Ganji Drinks, we respect your privacy and are committed to protecting your personal information.
                  This Privacy Policy explains how we collect, use, and protect your information when you visit our website
                  or purchase our traditional ragi drinks.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Information We Collect
                </h2>
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Personal Information:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      Name, email address, phone number, and delivery address when you place an order or contact us.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Order Information:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      Product preferences, order history, and payment information (processed securely by third-party payment providers).
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold mb-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>Technical Information:</h3>
                    <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                      IP address, browser type, device information, and usage data to improve our website experience.
                    </p>
                  </div>
                </div>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  How We Use Your Information
                </h2>
                <ul style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }} className="list-disc pl-6 space-y-2">
                  <li>To process and deliver your orders</li>
                  <li>To provide customer support and respond to inquiries</li>
                  <li>To improve our products and services</li>
                  <li>To send important updates about your orders</li>
                  <li>To ensure website security and prevent fraud</li>
                  <li>To comply with legal obligations</li>
                </ul>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Data Protection
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  We implement appropriate technical and organizational measures to protect your personal information
                  against unauthorized access, alteration, disclosure, or destruction. Your payment information is processed
                  through secure third-party payment processors.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Your Rights
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  You have the right to access, update, or delete your personal information. If you wish to exercise
                  these rights, please contact us at privacy@ganjidrinks.shop.
                </p>
              </section>

              <section>
                <h2
                  className="text-2xl font-bold mb-4"
                  style={{
                    color: "hsl(var(--ganji-rustic-brown))",
                    fontFamily: "'Permanent Marker', cursive"
                  }}
                >
                  Contact Us
                </h2>
                <p style={{ color: "hsl(var(--ganji-rustic-brown))", lineHeight: "1.6" }}>
                  If you have any questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="mt-4 space-y-2" style={{ color: "hsl(var(--ganji-rustic-brown))" }}>
                  <p>Email: ganjidrinks@gmail.com</p>
                  <p>Phone: +91 9019323880</p>
                  <p>Address: Bangalore, India</p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
